/**
 * flags-banners-showcase.js
 * Place in: assets/flags-banners-showcase.js
 *
 * Behaviour:
 *  - All hero + examples sections are always visible (no show/hide)
 *  - Clicking a tab smooth-scrolls to the matching hero section
 *  - Active tab highlights automatically as user scrolls (scroll event + getBoundingClientRect)
 *  - URL hash is updated on tab click (e.g. #customer-stories)
 *  - Swiper reads slides-per-view and arrow settings from data attributes
 *  - Arrows are hidden automatically when total slides fit within the current view
 *
 * This file is included by every flags section (flags-nav, flags-hero, flags-examples,
 * flags-banners-showcase), so a page can execute it many times. The guard below makes
 * sure the listeners are only bound once.
 */

(function () {
  'use strict';

  if (window.__fgFlagsShowcaseInit) return;
  window.__fgFlagsShowcaseInit = true;

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var scrollBehavior = prefersReducedMotion ? 'auto' : 'smooth';

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    enforceNonStickyNav();
    initScrollTabs();
    initSwipers();
  }

  // Keep nav non-sticky (inline styles as safety net; CSS handles the rest)
  function enforceNonStickyNav() {
    document.querySelectorAll('.flags-nav-wrapper').forEach(function (nav) {
      nav.style.setProperty('position', 'static', 'important');
      nav.style.setProperty('top', 'unset', 'important');
      nav.style.setProperty('z-index', 'auto', 'important');
      nav.style.setProperty('box-shadow', 'none', 'important');
    });
  }

  /** Trim + lowercase for case-insensitive matching */
  function normalise(str) {
    return str ? str.trim().toLowerCase() : '';
  }

  function navHeight() {
    var navEl = document.querySelector('.flags-section-nav');
    return navEl ? navEl.offsetHeight : 0;
  }

  /** The section a tab points to: by href id first, then by matching data-tab-label. */
  function findTarget(tab) {
    var href = tab.getAttribute('href');
    var targetId = href && href.startsWith('#') ? href.slice(1) : null;
    var target = targetId ? document.getElementById(targetId) : null;

    if (!target && tab.dataset.tabLabel) {
      target = document.querySelector(
        '.flags-cat--hero[data-tab-label="' + CSS.escape(tab.dataset.tabLabel) + '"]'
      );
    }

    return { id: targetId, el: target };
  }

  // ── Tab scroll + active highlight on scroll ──────────────────────────────
  function initScrollTabs() {
    var tabs = Array.prototype.slice.call(document.querySelectorAll('.flags-nav__tab'));
    if (!tabs.length) return;

    // ── Click: smooth-scroll to matching section ──
    tabs.forEach(function (tab) {
      tab.addEventListener('click', function (e) {
        e.preventDefault();

        var target = findTarget(tab);

        if (target.el) {
          // Update URL hash without page jump
          if (target.id) history.pushState(null, '', '#' + target.id);

          // Account for sticky nav height when scrolling
          var top = target.el.getBoundingClientRect().top + window.pageYOffset - navHeight() - 8;
          window.scrollTo({ top: top, behavior: scrollBehavior });

          // Move focus to the section for keyboard and screen-reader users
          if (!target.el.hasAttribute('tabindex')) target.el.setAttribute('tabindex', '-1');
          target.el.focus({ preventScroll: true });
        }

        setActiveTab(tab, tabs);
      });
    });

    // Sections to watch, in DOM order. Prefer tab href ids; fall back to hero panels matched by label.
    var panels = [];
    tabs.forEach(function (tab) {
      var href = tab.getAttribute('href');
      var section = href && href.startsWith('#') ? document.getElementById(href.slice(1)) : null;
      if (section) panels.push({ el: section, tab: tab });
    });

    if (!panels.length) {
      document.querySelectorAll('.flags-cat--hero').forEach(function (panel) {
        var label = normalise(panel.dataset.tabLabel);
        var tab = tabs.find(function (t) { return normalise(t.dataset.tabLabel) === label; });
        if (tab) panels.push({ el: panel, tab: tab });
      });
    }

    if (!panels.length) return;

    // ── Scroll: highlight tab whose section is nearest the top of viewport ──
    var ticking = false;

    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        highlightActiveTab(panels, tabs);
        ticking = false;
      });
    }, { passive: true });

    // Run once on load to set correct initial state
    highlightActiveTab(panels, tabs);
  }

  /**
   * The last section (in DOM order) whose top edge is at or above the trigger line
   * (nav height + a small buffer) wins.
   */
  function highlightActiveTab(panels, tabs) {
    var triggerLine = navHeight() + 40;
    var activeTab = null;

    panels.forEach(function (item) {
      if (item.el.getBoundingClientRect().top <= triggerLine) activeTab = item.tab;
    });

    if (activeTab && !activeTab.classList.contains('is-active')) setActiveTab(activeTab, tabs);
  }

  function setActiveTab(activeTab, tabs) {
    tabs.forEach(function (t) {
      var isActive = t === activeTab;
      t.classList.toggle('is-active', isActive);
      if (isActive) {
        t.setAttribute('aria-current', 'true');
      } else {
        t.removeAttribute('aria-current');
      }
    });

    // Scroll the active tab to the horizontal center of the nav strip
    var navInner = activeTab.closest('.flags-nav__inner');
    if (navInner) {
      var scrollTarget = activeTab.offsetLeft - (navInner.offsetWidth / 2) + (activeTab.offsetWidth / 2);
      navInner.scrollTo({ left: scrollTarget, behavior: scrollBehavior });
    }
  }

  // ── Hide arrows when all slides fit within the current slidesPerView ──────
  function updateArrows(swiper, prevEl, nextEl, showArrows) {
    if (!showArrows || !prevEl || !nextEl) return;

    var hide = swiper.slides.length <= swiper.params.slidesPerView;
    prevEl.style.display = hide ? 'none' : '';
    nextEl.style.display = hide ? 'none' : '';
  }

  /** Show the slider wrapper (flags-examples hides it until Swiper is ready to avoid a flash of unstyled slides). */
  function revealSwiper(el) {
    var wrapper = el.closest('.flags-examples-swiper-wrapper');
    if (wrapper) wrapper.classList.add('swiper-ready');
    el.dispatchEvent(new CustomEvent('swiperInit'));
  }

  // ── Swiper init ───────────────────────────────────────────────────────────
  function initSwipers() {
    var sliders = document.querySelectorAll('.flags-examples-swiper[data-swiper]');
    if (!sliders.length) return;

    if (typeof Swiper === 'undefined') {
      window.fgLoadSwiper().then(initSwipers, function () {
        // Swiper failed to load: show the slides as a static row rather than leave them hidden
        sliders.forEach(revealSwiper);
      });
      return;
    }

    sliders.forEach(function (el) {
      if (el.swiper) return;

      var wrapper = el.closest('.flags-examples-swiper-wrapper');

      var slidesDesktop = parseInt(el.dataset.slidesDesktop, 10) || 5;
      var slidesTablet  = parseInt(el.dataset.slidesTablet,  10) || 3;
      var slidesMobile  = parseInt(el.dataset.slidesMobile,  10) || 2;
      var showArrows    = el.dataset.showArrows !== 'false';

      var prevEl = (wrapper || el).querySelector('.flags-swiper-button-prev');
      var nextEl = (wrapper || el).querySelector('.flags-swiper-button-next');

      new Swiper(el, {
        slidesPerView: slidesMobile,
        spaceBetween: 16,
        loop: false,
        grabCursor: true,
        a11y: {
          prevSlideMessage: 'Previous design example',
          nextSlideMessage: 'Next design example'
        },
        navigation: showArrows && prevEl && nextEl
          ? { prevEl: prevEl, nextEl: nextEl }
          : false,
        pagination: {
          el: el.querySelector('.flags-swiper-pagination'),
          clickable: true,
          dynamicBullets: true
        },
        breakpoints: {
          640: { slidesPerView: slidesTablet,  spaceBetween: 20 },
          1024: { slidesPerView: slidesDesktop, spaceBetween: 24 }
        },
        on: {
          init: function (sw) {
            updateArrows(sw, prevEl, nextEl, showArrows);
            revealSwiper(el);
          },
          breakpoint: function (sw) {
            updateArrows(sw, prevEl, nextEl, showArrows);
          }
        }
      });
    });
  }

  // ── Sticky nav class toggle on scroll ────────────────────────────────────────
  var stickyTicking = false;
  window.addEventListener('scroll', function () {
    if (stickyTicking) return;
    stickyTicking = true;
    requestAnimationFrame(function () {
      stickyTicking = false;
      var el = document.querySelector('.flags-section-nav');
      if (!el) return;
      el.classList.toggle('is-sticky', el.getBoundingClientRect().top <= 0);
    });
  }, { passive: true });

})();
